import {
    ApplicationViewModel,
    EndorsementDocumentApiModel,
    EndorsementPositionModel,
    EndorsementRestrictionModel,
    IssueDocumentDetailsApiModel,
    LanguageOnPrintedEndorsement,
    PersonalInfoRequirementValueViewModel,
    PositionsClient,
    StcwRulesClient
} from "@/api-client";
import Handlebars from "handlebars";
import { formatDate } from "@/formatting/dateFormatter";

Handlebars.registerHelper('helperMissing', function(...args: any[]) {
    const options = args[args.length - 1] as {
        name: string;
        data: { root: Record<string, unknown> };
        lookupProperty: (parent: unknown, key: string) => unknown;
    };
    const rootValue = options.lookupProperty(options.data.root, options.name);
    if (rootValue === null) {
        return "";
    }
    return `UNKNOWN(${options.name})`;
});
import emptyImage from "@/assets/placeholders/single-pixel.gif";
import { isNullOrWhiteSpace } from "@/extensions/string.helpers";
import { PersonalInfoClient } from "@/api-client";
import { FileModel } from "@/api-client";

async function getImageUrl(file: FileModel | null | undefined, seafarerId: string){
    let response = emptyImage;
    if(file){
        response = (await new PersonalInfoClient().downloadFile(file.id, seafarerId)).url
    }
    return response;
}

export async function renderApplicationCertificatePrintPreviewTemplate(
    issueDocumentDetails: IssueDocumentDetailsApiModel,
    personalInfoRequirement: PersonalInfoRequirementValueViewModel,
    seafarerId: string,
    printTemplateUrl: string): Promise<string> {
    let fullUrl = printTemplateUrl;
    if (printTemplateUrl.startsWith('/')) {
        const settings = (await import('@/main')).settings;
        fullUrl = settings.misApiBaseUrl + printTemplateUrl;
    }
    const printTemplateFile = await fetch(fullUrl, { method: 'GET' });

    const profileImageUrl = await getImageUrl(personalInfoRequirement.profileImage, seafarerId);
    const signatureImageUrl = await getImageUrl(personalInfoRequirement.signatureImage, seafarerId);

    const template = Handlebars.compile(await printTemplateFile.text());
    /*eslint-disable */
    return template({
        SEERIANR: "",
        TUNNISTUSENR: "",
        TUNNISTUSNIMI: "",
        TUNNISTUSNAME: "",
        PERENIMI: personalInfoRequirement.lastName,
        EESNIMI: personalInfoRequirement.firstName,
        SYNNIKPV: formatDate(personalInfoRequirement.birthdate),
        ISIKUKOOD: personalInfoRequirement.personalCode,
        photo: profileImageUrl,
        signature: signatureImageUrl,
        KLOPP: formatDate((issueDocumentDetails as any).validUntil),
        VAKPV: formatDate(issueDocumentDetails?.issueDate),
        AMETIISIK: "",
        official_signature: emptyImage,
        QRKOOD: emptyImage,
        PAGE1: null,
        PAGE2: null,
        accessToken: "",
        LAEVANIMI: "",
        GT: "",
        GTENG: "",
        GTANKER: "",
        REEGEL: "",
        SOIDULIINID: "",
        SADAM: "",
        KOOL: "",
        KOOLENG: "",
        KOOLITUSEKUUPAEV: "",
        AMETENG: "",
        AMETEST: "",
        PIIRKONNAD: "",
        PIIRKONNADENG: "",
    })
    /*eslint-enable */
}

export async function renderApplicationEndorsementPrintPreviewTemplate(
    endorsement: EndorsementDocumentApiModel,
    personalInfoRequirement: PersonalInfoRequirementValueViewModel,
    seafarerId: string,
    printTemplateUrl: string): Promise<string> {


    const limitations: { responsibilities: string | undefined, level: string, joinedLimitations: string[] }[] = [];
    const positionOptions = await new PositionsClient().list();

    // Signed and submitted endorsements have duplicated overallRestrictions to every Position's restrictions too,
    // but unsigned / unsubmitted endorsements don't. Same thing with functions too.
    // Because of this I simulate this behaviour here by concatting overallLimitations to the end of functions and positions.
    // This means that if the logic for these overallRestrictions should ever change, then they should be separately changed here too.
    // This isn't the best solution and if there was more time would hopefully be done differently.

    const overallLimitationsEst = (endorsement.overallRestrictions.length ? endorsement.overallRestrictions.map(x => x.descriptionEst) : []);
    const overallLimitationsEng = (endorsement.overallRestrictions.length ? endorsement.overallRestrictions.map(x => x.descriptionEng) : []);
    const automaticLimitationsEst = (endorsement.automaticRestrictions.length ? endorsement.automaticRestrictions.map(x => x.descriptionEst) : []);
    const automaticLimitationsEng = (endorsement.automaticRestrictions.length ? endorsement.automaticRestrictions.map(x => x.descriptionEng) : []);

    const extraLimitationsEst = overallLimitationsEst.concat(automaticLimitationsEst);
    const extraLimitationsEng = overallLimitationsEng.concat(automaticLimitationsEng);

    function getValueOrDefault(value: string | undefined) {
        return isNullOrWhiteSpace(value) ? "-" : value;
    }

    function positionMap(endorsementPosition: EndorsementPositionModel) {
        const positions = [];
        const option = positionOptions.find(po => po.id.value == endorsementPosition.id);

        if (option) {
            positions.push({
                post: option.name.estonian.toUpperCase(),
                responsibility: getValueOrDefault(option.responsibilities?.estonian),
                // eslint-disable-next-line @typescript-eslint/camelcase
                post_level: getValueOrDefault(option.level.estonian),
                limitations: endorsementPosition.restrictions.map(r => r.descriptionEst).concat(extraLimitationsEst)
            });
            if (endorsement.languageOnPrint == LanguageOnPrintedEndorsement.EstonianAndEnglish) {
                positions.push({
                    post: option.name.english.toUpperCase(),
                    responsibility: getValueOrDefault(option.responsibilities?.english),
                    // eslint-disable-next-line @typescript-eslint/camelcase
                    post_level: getValueOrDefault(option.level.english),
                    limitations: endorsementPosition.restrictions.map(r => r.descriptionEng).concat(extraLimitationsEng)
                });
            }
        }
        return positions;
    }

    for (const position of endorsement.positions) {
        const positionOption = positionOptions.find(po => po.id.value == position.id);
        if (positionOption) {
            const matchingRestrictionEstonian = limitations.find(x => x.responsibilities == positionOption.responsibilities?.estonian
                && x.level == positionOption.level.estonian);

            if (matchingRestrictionEstonian) {
                matchingRestrictionEstonian.joinedLimitations.push(position.restrictions.map(x => x.descriptionEst).concat(extraLimitationsEst).join(". "));
            }
            else {
                limitations.push({
                    responsibilities: positionOption.responsibilities?.estonian, level: positionOption.level.estonian,
                    joinedLimitations: [(position.restrictions.length ? position.restrictions.map(x => x.descriptionEst) : []).concat(extraLimitationsEst).join(". ")]
                })
            }

            if (endorsement.languageOnPrint == LanguageOnPrintedEndorsement.EstonianAndEnglish) {
                const matchingRestrictionEnglish = limitations.find(x => x.responsibilities == positionOption.responsibilities?.english
                    && x.level == positionOption.level.english);

                if (matchingRestrictionEnglish) {
                    matchingRestrictionEnglish.joinedLimitations.push(position.restrictions.map(x => x.descriptionEng).concat(extraLimitationsEng).join(". "));
                }
                else {
                    limitations.push({
                        responsibilities: positionOption.responsibilities?.english, level: positionOption.level.english,
                        joinedLimitations: [(position.restrictions.length ? position.restrictions.map(x => x.descriptionEng) : []).concat(extraLimitationsEng).join(". ")]
                    })
                }
            }
        }
    }


    let fullEndorsementUrl = printTemplateUrl;
    if (printTemplateUrl.startsWith('/')) {
        const settings = (await import('@/main')).settings;
        fullEndorsementUrl = settings.misApiBaseUrl + printTemplateUrl;
    }
    const printTemplateFile = await fetch(fullEndorsementUrl, { method: 'GET' });
    const stcwCodes = (await new StcwRulesClient().list()).filter(r => endorsement.stcwRules.includes(r.id)).map(r => r.code);

    const positions = endorsement.positions.flatMap(positionMap);

    /*eslint-disable */
    const template = Handlebars.compile(await printTemplateFile.text());
    const profileImageUrl = await getImageUrl(personalInfoRequirement.profileImage, seafarerId);
    const signatureImageUrl = await getImageUrl(personalInfoRequirement.signatureImage, seafarerId);

    /*eslint-disable */
    return template({
        SEERIANR: endorsement?.certificateNumber + "-",
        TUNNISTUSNR: endorsement?.certificateNumber,
        PERENIMI: personalInfoRequirement.lastName,
        EESNIMI: personalInfoRequirement.firstName,
        SYNNIKPV: formatDate(personalInfoRequirement.birthdate),
        ISIKUKOOD: personalInfoRequirement.personalCode,
        photo: profileImageUrl,
        signature: signatureImageUrl,
        KEHTIVUSELOPP: formatDate(endorsement.validUntil),
        KEHTIVUSEALGUS: formatDate(endorsement.issueDate),
        AMETIISIK: "",
        official_signature: emptyImage,
        posts: positions,
        restrictions: limitations,
        REEGLID: stcwCodes.join(", "),
        QRKOOD: emptyImage,
        accessToken: "",
        TUNNISTUSRIIK: endorsement.certificateCountryNamePossessiveEst || "",
        TUNNISTUSRIIKENG: endorsement.certificateCountryNamePossessiveEng || "",
        PAGE1: null,
        PAGE2: null,
    })
    /*eslint-enable */
}
